import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Book, Calendar, Users } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { TestimonialsSection } from '../components/TestimonialsSection';
import { usePageContent } from '../hooks/usePageContent';
import { usePageTitle } from '../hooks/usePageTitle';
import { useT } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Home = () => {
  usePageTitle('');
  const [courses, setCourses] = useState([]);
  const [events, setEvents] = useState([]);
  const { t } = usePageContent('home');
  const tr = useT();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, eventsRes] = await Promise.all([
        axios.get(`${API_URL}/api/courses`),
        axios.get(`${API_URL}/api/events`),
      ]);
      setCourses(coursesRes.data.slice(0, 3));
      setEvents(eventsRes.data.slice(0, 3));
    } catch (error) {
      // Silently ignore — courses/events sections will just show empty state.
      // Users see a friendly "no items yet" instead of a stack trace in console.
    }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      <section
        className="relative h-screen flex items-center justify-center noise-texture"
        style={{
          backgroundImage: `url(${t('hero_image', 'https://images.unsplash.com/photo-1494548162494-384bba4ab999?q=85')})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal-900/60 via-charcoal-900/40 to-transparent" />
        <div className="relative z-10 text-center max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="text-saffron-500 uppercase text-sm md:text-base tracking-[0.2em] font-body font-semibold mb-6">
              {t('hero_overline', 'Eternal Wisdom for Modern Times')}
            </div>
            <h1
              className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-white tracking-tighter leading-none mb-6"
              data-testid="hero-title"
            >
              {t('hero_title', 'Discover the Timeless Path of')}
              <br />
              <span className="text-gold-500">{t('hero_highlight', 'Sanatana Dharma')}</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 font-body leading-relaxed mb-10 max-w-3xl mx-auto">
              {t('hero_description', 'Embark on a transformative journey through ancient Vedic teachings, spiritual practices, and the profound wisdom that has guided seekers for millennia.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/courses"
                data-testid="hero-cta-courses"
                className="bg-saffron-600 text-white rounded-full px-8 py-4 font-body font-medium hover:bg-saffron-500 transition-colors shadow-lg shadow-saffron-600/20 inline-flex items-center justify-center gap-2"
              >
                {t('cta_explore_courses', 'Explore Courses')} <ArrowRight size={20} />
              </Link>
              <Link
                to="/about"
                data-testid="hero-cta-about"
                className="bg-transparent border-2 border-white text-white rounded-full px-8 py-4 font-body font-medium hover:bg-white/10 transition-colors inline-flex items-center justify-center"
              >
                {t('cta_learn_more', 'Learn More')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-cream" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="text-saffron-600 uppercase text-xs md:text-sm tracking-[0.2em] font-body font-semibold mb-4">
                {t('intro_overline', 'Welcome to Our Foundation')}
              </div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight leading-snug text-charcoal-800 mb-6">
                {t('intro_heading', 'A Sanctuary for Spiritual Growth and Vedic Learning')}
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-charcoal-600 mb-6">
                {t('intro_text_1', 'The Rishidev Foundation is dedicated to preserving and sharing the timeless wisdom of Sanatana Dharma. Through authentic teachings, sacred practices, and a supportive community, we guide seekers on their spiritual journey.')}
              </p>
              <p className="text-base md:text-lg leading-relaxed text-charcoal-600 mb-8">
                {t('intro_text_2', 'Our mission is to make the profound knowledge of the Vedas, Upanishads, and Bhagavad Gita accessible to all who seek truth, purpose, and inner peace.')}
              </p>
              <Link
                to="/about"
                data-testid="intro-learn-more"
                className="inline-flex items-center gap-2 text-saffron-600 font-body font-semibold hover:text-saffron-500 transition-colors"
              >
                {tr('Discover Our Mission')} <ArrowRight size={20} />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src={t('intro_image', 'https://images.unsplash.com/photo-1665003725647-3ae0f01140b1?q=85')}
                alt="Introduction to our foundation"
                className="rounded-xl shadow-[0_8px_30px_rgba(44,30,22,0.12)] w-full"
                data-testid="home-intro-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white" data-testid="quick-links-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="text-saffron-600 uppercase text-xs md:text-sm tracking-[0.2em] font-body font-semibold mb-4">
              {tr('Explore Our Offerings')}
            </div>
            <h2 className="text-3xl md:text-4xl font-heading tracking-tight leading-snug text-charcoal-800">
              {tr('Begin Your Spiritual Journey')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Link
                to="/courses"
                data-testid="quick-link-courses"
                className="block bg-white rounded-xl shadow-[0_8px_30px_rgba(44,30,22,0.06)] border border-charcoal-100 p-8 hover:shadow-[0_12px_40px_rgba(44,30,22,0.12)] transition-all duration-300 group"
              >
                <div className="w-14 h-14 bg-saffron-100 rounded-full flex items-center justify-center mb-6 group-hover:bg-saffron-200 transition-colors">
                  <Book className="text-saffron-600" size={28} />
                </div>
                <h3 className="text-xl md:text-2xl font-heading leading-snug text-charcoal-800 mb-4">
                  {tr('Sacred Courses')}
                </h3>
                <p className="text-base leading-relaxed text-charcoal-600 mb-4">
                  {tr('Dive deep into Vedic philosophy, Bhagavad Gita studies, meditation practices, and the wisdom of the Upanishads.')}
                </p>
                <span className="inline-flex items-center gap-2 text-saffron-600 font-body font-semibold group-hover:gap-3 transition-all">
                  {tr('View Courses')} <ArrowRight size={18} />
                </span>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Link
                to="/events"
                data-testid="quick-link-events"
                className="block bg-white rounded-xl shadow-[0_8px_30px_rgba(44,30,22,0.06)] border border-charcoal-100 p-8 hover:shadow-[0_12px_40px_rgba(44,30,22,0.12)] transition-all duration-300 group"
              >
                <div className="w-14 h-14 bg-gold-100 rounded-full flex items-center justify-center mb-6 group-hover:bg-gold-500/20 transition-colors">
                  <Calendar className="text-gold-600" size={28} />
                </div>
                <h3 className="text-xl md:text-2xl font-heading leading-snug text-charcoal-800 mb-4">
                  {tr('Events & Satsangs')}
                </h3>
                <p className="text-base leading-relaxed text-charcoal-600 mb-4">
                  {tr('Join our spiritual gatherings, webinars, and festival celebrations to connect with fellow seekers.')}
                </p>
                <span className="inline-flex items-center gap-2 text-saffron-600 font-body font-semibold group-hover:gap-3 transition-all">
                  {tr('View Events')} <ArrowRight size={18} />
                </span>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Link
                to="/mentor"
                data-testid="quick-link-mentor"
                className="block bg-white rounded-xl shadow-[0_8px_30px_rgba(44,30,22,0.06)] border border-charcoal-100 p-8 hover:shadow-[0_12px_40px_rgba(44,30,22,0.12)] transition-all duration-300 group"
              >
                <div className="w-14 h-14 bg-saffron-100 rounded-full flex items-center justify-center mb-6 group-hover:bg-saffron-200 transition-colors">
                  <Users className="text-saffron-600" size={28} />
                </div>
                <h3 className="text-xl md:text-2xl font-heading leading-snug text-charcoal-800 mb-4">
                  {tr('Our Mentor')}
                </h3>
                <p className="text-base leading-relaxed text-charcoal-600 mb-4">
                  {tr('Learn from an enlightened teacher with decades of experience in Vedic traditions and spiritual practice.')}
                </p>
                <span className="inline-flex items-center gap-2 text-saffron-600 font-body font-semibold group-hover:gap-3 transition-all">
                  {tr('Meet Our Guru')} <ArrowRight size={18} />
                </span>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {courses.length > 0 && (
        <section className="py-24 md:py-32 bg-cream" data-testid="featured-courses-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex justify-between items-end mb-12">
              <div>
                <div className="text-saffron-600 uppercase text-xs md:text-sm tracking-[0.2em] font-body font-semibold mb-4">
                  {tr('Featured Teachings')}
                </div>
                <h2 className="text-3xl md:text-4xl font-heading tracking-tight leading-snug text-charcoal-800">
                  {tr('Popular Courses')}
                </h2>
              </div>
              <Link
                to="/courses"
                className="hidden md:inline-flex items-center gap-2 text-saffron-600 font-body font-semibold hover:text-saffron-500 transition-colors"
              >
                {tr('View All')} <ArrowRight size={20} />
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {courses.map((course, index) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-[0_8px_30px_rgba(44,30,22,0.06)] border border-charcoal-100 overflow-hidden hover:shadow-[0_12px_40px_rgba(44,30,22,0.12)] transition-all duration-300"
                >
                  <img
                    src={course.image_url}
                    alt={course.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <div className="bg-saffron-50 text-saffron-600 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider inline-block mb-3">
                      {tr(course.category)}
                    </div>
                    <h3 className="text-xl md:text-2xl font-heading leading-snug text-charcoal-800 mb-3">
                      {tr(course.title)}
                    </h3>
                    <p className="text-sm text-charcoal-600 leading-relaxed mb-4 line-clamp-2">
                      {tr(course.description)}
                    </p>
                    <div className="text-lg font-body font-semibold text-saffron-600 mb-4" data-testid={`home-course-fee-${course.id}`}>
                      {course.pricing_mode === 'fee' && course.fee_amount > 0
                        ? `₹${(course.fee_amount / 100).toLocaleString('en-IN')}`
                        : course.pricing_mode === 'dakshina'
                          ? tr('Dakshina (any amount)')
                          : tr('Free')}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <TestimonialsSection />

      <section
        className="py-24 md:py-32 relative noise-texture"
        style={{
          backgroundImage: `url(${t('cta_image', 'https://images.unsplash.com/photo-1633081517593-5649d73318af?q=85')})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="cta-section"
      >
        <div className="absolute inset-0 bg-charcoal-900/70" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-white tracking-tight leading-tight mb-6">
              {t('cta_heading', 'Join Our Spiritual Community')}
            </h2>
            <p className="text-lg md:text-xl text-white/90 font-body leading-relaxed mb-10">
              {t('cta_description', 'Subscribe to our newsletter for spiritual insights, upcoming events, and exclusive teachings delivered to your inbox.')}
            </p>
            <Link
              to="/contact"
              data-testid="cta-contact"
              className="bg-saffron-600 text-white rounded-full px-10 py-4 font-body font-medium hover:bg-saffron-500 transition-colors shadow-lg inline-flex items-center gap-2"
            >
              {tr('Get Started')} <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};